import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { recentActivitiesQuery } from '@atlassian/search-client/recent-activities';

import type { PrimaryProductKey } from '../../../common/constants/product-keys';
import type { ThirdPartyConfigsBootstrap } from '../../../common/constants/schemas/3p-config';
import { type SearchLinkProps } from '../../../common/ui/search-link/types';
import { filterSearchResultsWithHighlighting } from '../../../common/utils/filter-search-results';
import { type onError } from '../../error-boundary/log-exception';

import { recentViewsResultMapper } from './recent-views-result-mapper';

export const prefetchRecentViewsResults: (
	cloudId: string,
	onError: onError,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	primaryProduct?: PrimaryProductKey,
	activationId?: string,
) => Promise<SearchLinkProps[]> = async (
	cloudId: string,
	onError: onError,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	primaryProduct?: PrimaryProductKey,
	activationId?: string,
) => {
	const headers = buildAtlAttributionHeaders({
		product: primaryProduct,
		cloudId,
		activationId,
	});

	const fetchData = async (
		filteredProducts: string[],
		filteredObjectTypes: string[],
		filteredRootContainerIds: string[],
		filteredEventTypes: string[],
		itemsLimit: number,
	) => {
		const response = await recentActivitiesQuery({
			variables: {
				filteredProducts,
				filteredObjectTypes,
				filteredEventTypes,
				filteredRootContainerIds,
				itemsLimit,
			},
			headers,
		}).catch((error) => {
			onError(error);
		});

		const data = response && response.data;

		const edges = data?.activity?.myActivity?.all?.edges || [];
		return edges
			?.filter((edge) => edge.node?.object?.data)
			.map((edge) => recentViewsResultMapper(edge, thirdPartyConfigs));
	};

	return await fetchData(
		['confluence', 'jira', 'townsquare'],
		['page', 'issue', 'project'],
		[`ari:cloud:platform::site/${cloudId}`, `ari:cloud:townsquare::site/${cloudId}`],
		[],
		200,
	);
};

export const filterRecentViewsResults: (
	recentViews: SearchLinkProps[],
	inputQuery: string,
) => SearchLinkProps[] = (recentViews: SearchLinkProps[], inputQuery: string) => {
	if (inputQuery.length === 0) {
		return recentViews;
	}
	return recentViews && filterSearchResultsWithHighlighting(recentViews, inputQuery);
};
